/* assets/ilf-texture-gallery.js
 * Multi-panel (1-6) texture reveal with optional tabs, implemented as a
 * <ilf-texture-gallery> custom element. No external dependency.
 *
 * Geometry: separators are spaced equally along the CENTER line (y=50%):
 * mid[i] = i/count. Every separator is parallel, sharing one horizontal offset
 * `slant` (0.36/count of the stage width): its top point sits at mid + slant/2
 * and its bottom at mid - slant/2. This makes the left and right panels
 * 180deg-rotational partners and centers the middle parallelogram's centroid
 * on the container. At rest every tone shows as its band, clipped by the SVG
 * polygon the section renders (--band-clip).
 *
 * Two reveal engines share the events (RevealBase), picked per section from
 * data-effect:
 * - ClassicReveal (classic): the hovered band opens in place
 *   while its neighbours are pushed out — a tiny requestAnimationFrame tween
 *   (power4.out) rewrites the SVG polygons.
 * - LayeredReveal (the effects in EFFECTS): the hovered tone grows from its
 *   band to the whole stage over the other bands, or over the tone shown
 *   before, with the effect's motion. Every animation runs on the Web
 *   Animations API; most effects build temporary pieces (copies of a tone's
 *   image, or the Liquid spill's SVG) in the panel's .fx-pieces layer —
 *   Heart flurry its hearts in the .ilf-fx layer itself — and remove them
 *   when the transition ends.
 *
 * State is expressed through classes only: `is-active` on the shown tone's
 * wrapper / label / control button and `has-active` on its panel; the layered
 * engine adds `is-full` (wrapper shows the whole stage) and `is-leaving`
 * (wrapper collapsing back to its band). The CSS owns stacking order, the
 * control button's scale transition and the label fade.
 *
 * Each `.ilf-tab-panel` is its own reveal unit; the custom element owns the
 * panels + tab bar and tears down its document listeners in
 * disconnectedCallback so nothing leaks when the theme editor re-renders it.
 *
 * Buttons: real <button>s (hover, tap, focus or Enter shows the tone). The
 * Liquid pill reads is-forward / is-backward and step-a / step-b from the
 * .btn-row (markRow), the Segment bar thumb reads at-<index> (the last tone
 * shown); Heat touch and Tilt glass add pointer-driven motion through
 * BUTTON_FX, also on the Web Animations API.
 *
 * Tab switching: the underline slides to the chosen tab at once (CSS, keyed on
 * is-active); the new panel waits until its images are decoded (at most
 * PANEL_READY_TIMEOUT), then fades in over the current one. Hidden panels'
 * lazy images start loading only on intent (pointer / focus / touch on their
 * tab), so visitors who never switch tabs never download them.
 */

(function () {
  if (customElements.get('ilf-texture-gallery')) return;

  const SLANT_RATIO = 0.36; // separator offset per stage width, divided by the band count

  // Effect setting value → LayeredReveal method.
  const EFFECTS = {
    'band-expand': 'expand',
    'domino-cascade': 'cascade',
    'liquid-spill': 'spill',
    'soft-spread': 'soft',
    'twin-laser': 'laser',
    'part-bands': 'push',
    'venetian-ripple': 'slices',
    'mosaic-ripple': 'tiles',
    'origami-unfold': 'fold',
    'crystal-growth': 'crystal',
    'glitch-stream': 'glitch',
    'shatter-fall': 'shatter',
    'heart-flurry': 'flurry'
  };

  // The Fansly logo heart drawn by Heart flurry.
  const FANSLY_HEART = {
    viewBox: '0 0 143.69 117.86',
    path: 'M34.65.03c-8.31.35-16.75,2.98-22.98,8.65C4.45,14.98,1.05,24.53,0,33.81v2.95c.39,7.98,3.83,15.63,9.41,21.32,16.82,17.06,33.81,33.95,50.79,50.84,3.19,3.14,6.14,6.63,10.04,8.94h2.9c4.29-1.48,6.93-5.41,10.16-8.34,12.5-12.75,25.54-24.97,37.74-38l-36.6-36.73c4.13-4.14,8.22-8.33,12.42-12.39,3.41-3.38,8.18-5.34,13.01-4.95,9.82.13,17.95,10.17,16.14,19.82-1.06,8.38-8.94,12.93-13.87,18.96l12.37,11.79c4.12-3.38,7.98-7.54,11.48-11.56,9.78-11.52,10.27-29.64,1.33-41.79-5.85-8.53-15.75-13.83-26-14.67h-5.08c-7.58.86-15.06,3.73-20.7,8.96-16.97,16.55-33.58,33.47-50.57,50-3.94-3.88-7.85-7.78-11.76-11.67-4.15-4.13-6.35-10.25-5.14-16.06,1.66-8.63,10.55-15.12,19.31-13.67,8.44.9,12.97,8.99,19.06,13.86l12.23-11.61C61.08,10.12,50.81.76,37.83.03h-3.18ZM72.2,54.3c8.27.09,16.2,6.64,17.05,15.06,1.64,10.45-8.33,20.74-18.86,19.31-10.27-.36-17.78-11.6-15.21-21.31,1.48-6.4,6.99-11.58,13.47-12.68,1.17-.26,2.36-.38,3.54-.37h0ZM72.02,60.31c-6.13,0-11.1,4.97-11.1,11.1s4.97,11.1,11.1,11.1,11.1-4.97,11.1-11.1c0-1.65-.37-3.29-1.08-4.78-.8.56-1.75.87-2.72.87-2.62,0-4.74-2.12-4.74-4.74,0-.7.17-1.4.48-2.03-.99-.28-2.01-.42-3.03-.42h-.01Z'
  };

  // Geometry helpers: positions are fractions of the stage (0-1) written as
  // percentages, so every shape follows the stage size without measuring it.
  const pct = (value) => `${(value * 100).toFixed(3)}%`;
  const mix = (from, to, t) => from + (to - from) * t;
  const midLeft = (edges) => (edges.lt + edges.lb) / 2;
  const midRight = (edges) => (edges.rt + edges.rb) / 2;
  const shiftX = (x) => `translateX(${pct(x)})`;
  const polygon = (edges) =>
    `polygon(${pct(edges.lt)} 0%, ${pct(edges.rt)} 0%, ${pct(edges.rb)} 100%, ${pct(edges.lb)} 100%)`;
  // Polygon through [x, y] points given as stage fractions.
  const polygonPoints = (points) => `polygon(${points.map(([x, y]) => `${pct(x)} ${pct(y)}`).join(', ')})`;

  // Deterministic 0-1 noise from a number: an effect's jagged or scattered
  // details come out the same on every run.
  const seeded = (n) => {
    const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
    return x - Math.floor(x);
  };

  // The effects are designed on a 1152 x 316 stage; DESIGN scales their pixel
  // details (jitter, fall distance, perspective) to the real stage.
  const DESIGN = { width: 1152, height: 316 };
  let liquidCount = 0; // unique ids for each Liquid spill's SVG filters

  // Shared by both engines: element lookup, active classes, hover / touch events.
  class RevealBase {
    constructor(panel) {
      this.panel = panel;
      this.count = parseInt(panel.getAttribute('data-count'), 10) || 0;
      this.wrappers = Array.from(panel.querySelectorAll('.ilf-reveal-image-wrapper'));
      this.labels = Array.from(panel.querySelectorAll('.ilf-label'));
      this.buttons = Array.from(panel.querySelectorAll('.ilf-control-btn'));
      this.row = panel.querySelector('.btn-row'); // button styles other than Dots
      this.activeTile = null;          // integer index or 'COLLAPSE'
      this.outsideTapHandler = null;   // stored so it can be removed on destroy
      this.buttonFx = null;            // style-specific button motion (Heat touch, Tilt glass)
      this.rowIndex = -1;              // last tone marked on the button row
      this.rowStep = false;
      this.rowAt = -1;                 // last tone shown, kept while the tones are closed
    }

    // Toggle the classes that the CSS effect styles hook into.
    setActiveClasses(mode) {
      const activeIndex = mode === 'COLLAPSE' ? -1 : mode;
      this.panel.classList.toggle('has-active', activeIndex >= 0);
      [this.wrappers, this.labels, this.buttons].forEach((group) => {
        group.forEach((element, i) => element.classList.toggle('is-active', i === activeIndex));
      });
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', i === activeIndex ? 'true' : 'false'));
      this.markRow(activeIndex);
      if (this.buttonFx) this.buttonFx.activeChanged(activeIndex);
    }

    // On the button row: the direction of the move (is-forward / is-backward)
    // and step-a / step-b alternating on every change, so the Liquid pill
    // highlight stretches toward the new tone and replays its squish; and
    // at-<index> for the last tone shown, so the Segment bar thumb fades out
    // where it is when the tones close.
    markRow(activeIndex) {
      if (!this.row || activeIndex === this.rowIndex) return;
      const between = this.rowIndex >= 0 && activeIndex >= 0;
      this.row.classList.toggle('is-forward', between && activeIndex > this.rowIndex);
      this.row.classList.toggle('is-backward', between && activeIndex < this.rowIndex);
      this.rowStep = !this.rowStep;
      this.row.classList.toggle('step-a', this.rowStep);
      this.row.classList.toggle('step-b', !this.rowStep);
      if (activeIndex >= 0) {
        this.row.classList.remove(`at-${this.rowAt}`);
        this.row.classList.add(`at-${activeIndex}`);
        this.rowAt = activeIndex;
      }
      this.rowIndex = activeIndex;
    }

    bindEvents() {
      this.wrappers.forEach((wrapperEl, i) => {
        const buttonEl = this.buttons[i];

        if (buttonEl) {
          // Touch: tap a button to view, tap again to collapse.
          buttonEl.addEventListener('touchstart', (event) => {
            if (event.cancelable) event.preventDefault();
            this.animateTo(this.activeTile === i ? 'COLLAPSE' : i);
          }, { passive: false });
          // Mouse: hover a button to view.
          buttonEl.addEventListener('mouseenter', () => this.animateTo(i));
          // Keyboard: focusing a button shows its tone; Enter / Space too.
          buttonEl.addEventListener('focus', () => this.animateTo(i));
          buttonEl.addEventListener('click', () => this.animateTo(i));
        }

        // Touch (passive so a drag can still scroll); Mouse hover.
        wrapperEl.addEventListener('touchstart', () => this.hoverBand(i), { passive: true });
        wrapperEl.addEventListener('mouseenter', () => this.hoverBand(i));
      });

      // Collapse when the cursor leaves this panel, or keyboard focus does.
      this.panel.addEventListener('mouseleave', () => this.animateTo('COLLAPSE'));
      this.panel.addEventListener('focusout', (event) => {
        if (!this.panel.contains(event.relatedTarget)) this.animateTo('COLLAPSE');
      });

      // Collapse when tapping anywhere outside this panel. (Document-level, so
      // it's stored and removed in destroy().)
      this.outsideTapHandler = (event) => {
        if (!this.panel.contains(event.target)) this.animateTo('COLLAPSE');
      };
      document.addEventListener('touchstart', this.outsideTapHandler, { passive: true });
    }

    // Pointer entered an image slice (the buttons call animateTo directly).
    hoverBand(index) {
      this.animateTo(index);
    }

    // Remove anything attached outside this panel's own subtree.
    destroy() {
      if (this.outsideTapHandler) {
        document.removeEventListener('touchstart', this.outsideTapHandler);
        this.outsideTapHandler = null;
      }
    }
  }

  // Classic / Classic + Label: the hovered band opens in place.
  class ClassicReveal extends RevealBase {
    constructor(panel) {
      super(panel);

      // Geometry / timing constants (0-1 coordinate space).
      this.config = {
        slantRatio: SLANT_RATIO,
        overshoot: 0.25,  // how far an opening slice pushes past the container edges
        duration: 800     // ms
      };

      this.polys = Array.from(panel.querySelectorAll('.ilf-poly'));
      this.mids = [];                  // length count+1 separator positions at y=50%
      this.slant = 0;                  // current shared top-to-bottom offset
      this.animationFrame = null;

      this.init();
    }

    baseMids() {
      const mids = [];
      for (let i = 0; i <= this.count; i++) mids.push(i / this.count);
      return mids;
    }

    init() {
      if (this.count < 1) return;
      this.mids = this.baseMids();
      this.slant = this.config.slantRatio / this.count;
      this.render();
      if (this.count > 1) this.bindEvents(); // nothing to reveal with a single slice
    }

    // Top / bottom x for separator `index`. Container edges stay vertical.
    topX(index) {
      if (index === 0) return 0;
      if (index === this.count) return 1;
      return this.mids[index] + this.slant / 2;
    }

    botX(index) {
      if (index === 0) return 0;
      if (index === this.count) return 1;
      return this.mids[index] - this.slant / 2;
    }

    // Rewrite every clip polygon from the current mid/slant state.
    render() {
      for (let i = 0; i < this.count; i++) {
        const points =
          `${this.topX(i)},0 ${this.topX(i + 1)},0 ${this.botX(i + 1)},1 ${this.botX(i)},1`;
        if (this.polys[i]) this.polys[i].setAttribute('points', points);
      }
    }

    // Minimal tween — the only animation-engine code of this reveal.
    tween({ duration, onUpdate, onComplete }) {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      const ease = (progress) => 1 - Math.pow(1 - progress, 4); // power4.out
      let startTime;
      const step = (now) => {
        if (startTime === undefined) startTime = now;
        const progress = Math.min((now - startTime) / duration, 1);
        onUpdate(ease(progress));
        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(step);
        } else {
          this.animationFrame = null;
          if (onComplete) onComplete();
        }
      };
      this.animationFrame = requestAnimationFrame(step);
    }

    animateTo(mode) {
      if (this.activeTile === mode) return;
      this.activeTile = mode;

      // Capture the current state so an interrupting call tweens from here.
      const fromMids = this.mids.slice();
      const fromSlant = this.slant;

      // Targets. (Edge entries are ignored by topX/botX, so they need no special
      // handling here.)
      const toMids = [];
      let toSlant;
      if (mode === 'COLLAPSE') {
        for (let i = 0; i <= this.count; i++) toMids.push(i / this.count);
        toSlant = this.config.slantRatio / this.count;
      } else {
        const overshoot = this.config.overshoot;
        for (let i = 0; i <= this.count; i++) toMids.push(i <= mode ? -overshoot : 1 + overshoot);
        toSlant = 0;
      }

      this.setActiveClasses(mode);

      this.tween({
        duration: this.config.duration,
        onUpdate: (eased) => {
          for (let i = 0; i <= this.count; i++) {
            this.mids[i] = fromMids[i] + (toMids[i] - fromMids[i]) * eased;
          }
          this.slant = fromSlant + (toSlant - fromSlant) * eased;
          this.render();
        }
      });
    }

    // Snap back to the collapsed base state instantly (used when a tab is hidden).
    reset() {
      if (this.count < 1) return;
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.activeTile = null;
      this.mids = this.baseMids();
      this.slant = this.config.slantRatio / this.count;
      this.setActiveClasses('COLLAPSE');
      this.render();
    }

    destroy() {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      super.destroy();
    }
  }

  // The layered effects: the hovered tone grows from its band to the stage.
  class LayeredReveal extends RevealBase {
    constructor(panel, effect) {
      super(panel);
      this.effect = EFFECTS[effect];
      this.stage = panel.querySelector('.ilf-reveal-container');
      this.fx = {
        root: panel.querySelector('.ilf-fx'),
        pieces: panel.querySelector('.fx-pieces'),
        edgeLeft: panel.querySelector('.fx-edge--left'),
        edgeRight: panel.querySelector('.fx-edge--right'),
        laserLeft: panel.querySelector('.fx-laser--left'),
        laserRight: panel.querySelector('.fx-laser--right'),
        laserLabel: panel.querySelector('.fx-laser__label'),
        lines: panel.querySelector('.fx-lines')
      };

      // Fully open: both edges past the stage, keeping the band slant so the
      // glowing edges leave the stage parallel to themselves.
      this.slant = SLANT_RATIO / Math.max(this.count, 1);
      this.full = { lt: -this.slant, lb: -2 * this.slant, rt: 1 + 2 * this.slant, rb: 1 + this.slant };

      this.run = null; // the transition in flight: { kind, target, shape, animations, pieces }
      if (this.count > 1) this.bindEvents();
    }

    // Band `index` as fractions of the stage. The outer edges of the first and
    // last bands already sit at the open position (the stage clips them), so
    // they stay put while the band opens.
    band(index) {
      const last = this.count - 1;
      const half = this.slant / 2;
      return {
        lt: index === 0 ? this.full.lt : index / this.count + half,
        lb: index === 0 ? this.full.lb : index / this.count - half,
        rt: index === last ? this.full.rt : (index + 1) / this.count + half,
        rb: index === last ? this.full.rb : (index + 1) / this.count - half
      };
    }

    // The image opens a tone only while none is shown. During a reveal the
    // other slices stay under the pointer (the previous tone, the bands the
    // effect is drawing over), so following them would flip back and forth;
    // once a tone is open the buttons switch it and leaving the panel closes it.
    hoverBand(index) {
      if (Number.isInteger(this.activeTile)) return;
      this.animateTo(index);
    }

    animateTo(mode) {
      if (this.activeTile === mode) return;
      const from = Number.isInteger(this.activeTile) ? this.activeTile : -1;
      this.activeTile = mode;
      this.setActiveClasses(mode);
      if (mode === 'COLLAPSE') this.collapse(from);
      else this.reveal(mode, from);
    }

    reveal(to, from) {
      const target = this.wrappers[to];
      // A tone hovered again while it collapses grows on from its current edges.
      const resume = this.run && this.run.kind === 'collapse' && this.run.target === to
        ? getComputedStyle(target).clipPath
        : null;
      this.finishRun();
      if (this.reducedMotion()) {
        this.showFull(to);
        return;
      }

      const fromAll = from < 0;
      const run = this.startRun('reveal', to);
      // From another tone: the new tone's band fades in over it first.
      if (!fromAll) this.play(run, target, [{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });

      this[this.effect]({
        run,
        to,
        from,
        fromAll,
        target,
        previous: fromAll ? null : this.wrappers[from],
        band: this.band(to),
        center: (to + 0.5) / this.count,
        start: resume,
        lead: fromAll ? 0 : 180
      });
      this.whenDone(run);
    }

    // Shrink the shown tone back to its band; the other bands brighten as they reappear.
    collapse(from) {
      const run = this.run;
      // Collapsing mid-reveal: carry on from where the band edges are now.
      let start = null;
      if (run && run.kind === 'reveal' && run.target === from && run.shape === 'band') {
        start = {
          clip: getComputedStyle(this.wrappers[from]).clipPath,
          left: this.currentTransform(this.fx.edgeLeft),
          right: this.currentTransform(this.fx.edgeRight)
        };
      }
      this.finishRun();
      if (from < 0) return;

      const target = this.wrappers[from];
      target.classList.remove('is-full');
      if (this.reducedMotion()) return;

      target.classList.add('is-leaving');
      const next = this.startRun('collapse', from);
      const band = this.band(from);
      const timing = { duration: 900, easing: 'cubic-bezier(.65,0,.35,1)' };
      this.play(next, target, [{ clipPath: start?.clip || polygon(this.full) }, { clipPath: polygon(band) }], timing);
      this.play(next, this.fx.edgeLeft, [
        { transform: start?.left || shiftX(midLeft(this.full)), opacity: 1 },
        { transform: shiftX(midLeft(band)), opacity: 0 }
      ], timing);
      this.play(next, this.fx.edgeRight, [
        { transform: start?.right || shiftX(midRight(this.full)), opacity: 1 },
        { transform: shiftX(midRight(band)), opacity: 0 }
      ], timing);
      this.wrappers.forEach((wrapper, index) => {
        if (index === from) return;
        this.play(next, wrapper, [{ filter: 'brightness(0.7)' }, { filter: 'brightness(1)' }], { duration: 900, easing: 'ease' });
      });
      this.whenDone(next);
    }

    // Snap back to every band at rest (used when a tab is hidden).
    reset() {
      this.finishRun();
      this.activeTile = null;
      this.setActiveClasses('COLLAPSE');
      this.showBands();
    }

    destroy() {
      this.finishRun();
      super.destroy();
    }

    // ---- Transition bookkeeping ----

    startRun(kind, target) {
      this.run = { kind, target, shape: null, animations: [], pieces: [] };
      return this.run;
    }

    play(run, element, keyframes, options) {
      if (!element) return null;
      const animation = element.animate(keyframes, { fill: 'both', ...options });
      run.animations.push(animation);
      return animation;
    }

    whenDone(run) {
      Promise.all(run.animations.map((animation) => animation.finished))
        .then(() => {
          if (this.run === run) this.finishRun();
        })
        .catch(() => {}); // cancelled by a newer transition, which settled it
    }

    // End the transition in flight at once, in its final resting state.
    finishRun() {
      const run = this.run;
      if (!run) return;
      this.run = null;
      run.animations.forEach((animation) => animation.cancel());
      run.pieces.forEach((piece) => piece.remove());
      if (this.fx.pieces) this.fx.pieces.className = 'fx-pieces';
      if (run.kind === 'reveal') this.showFull(run.target);
      else this.showBands();
    }

    showFull(index) {
      this.wrappers.forEach((wrapper, i) => {
        wrapper.classList.toggle('is-full', i === index);
        wrapper.classList.remove('is-leaving');
      });
    }

    showBands() {
      this.wrappers.forEach((wrapper) => wrapper.classList.remove('is-full', 'is-leaving'));
    }

    currentTransform(element) {
      if (!element) return null;
      const transform = getComputedStyle(element).transform;
      return transform === 'none' ? null : transform;
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // ---- Temporary pieces: copies of a tone's image in the .fx-pieces layer ----

    cloneImage(index) {
      const source = this.wrappers[index].querySelector('img');
      const image = document.createElement('img');
      image.className = 'fx-image';
      image.alt = '';
      // The resource the band already shows, so no other size is downloaded.
      if (source) image.src = source.currentSrc || source.src;
      return image;
    }

    // A full-stage copy of the tone's image.
    addPiece(run, index) {
      const piece = this.cloneImage(index);
      piece.classList.add('fx-piece');
      this.fx.pieces.append(piece);
      run.pieces.push(piece);
      return piece;
    }

    // A grid cell showing its part of the tone's image: the image spans the
    // whole stage (100cqw x 100cqh) and is shifted by `placement`.
    addCell(run, index, className, placement, duration) {
      const cell = document.createElement('div');
      cell.className = className;
      const image = this.cloneImage(index);
      cell.append(image);
      this.fx.pieces.append(cell);
      run.pieces.push(cell);
      this.play(run, image, [{ transform: placement }, { transform: placement }], { duration });
      return cell;
    }

    // The stage's pixel size, its scale against the design stage, and the
    // band slant as a tangent (horizontal offset per unit of height).
    stageMetrics() {
      const { width, height } = this.stage.getBoundingClientRect();
      return {
        width,
        height,
        sx: width / DESIGN.width,
        sy: height / DESIGN.height,
        tan: height ? (this.slant * width) / height : 0
      };
    }

    moveEdges(run, left, right, band, timing) {
      this.play(run, left, [
        { transform: shiftX(midLeft(band)), opacity: 1 },
        { transform: shiftX(midLeft(this.full)), opacity: 1 }
      ], timing);
      this.play(run, right, [
        { transform: shiftX(midRight(band)), opacity: 1 },
        { transform: shiftX(midRight(this.full)), opacity: 1 }
      ], timing);
    }

    // ---- Effects: each receives the reveal context ----

    // Band expand: the band's edges glide out to the stage edges, lit by glow lines.
    expand({ run, target, band, start, lead }) {
      run.shape = 'band';
      const timing = { duration: 1000, delay: lead, easing: 'cubic-bezier(.65,0,.35,1)' };
      this.play(run, target, [{ clipPath: start || polygon(band) }, { clipPath: polygon(this.full) }], timing);
      this.moveEdges(run, this.fx.edgeLeft, this.fx.edgeRight, band, timing);
    }

    // Domino cascade: band after band flips to the new tone, outward from it.
    cascade({ run, to, lead }) {
      for (let index = 0; index < this.count; index++) {
        if (index === to) continue;
        const band = this.band(index);
        // A sliver on the side facing the shown tone widens to the whole band.
        const sliver = index > to
          ? { lt: band.lt, rt: band.lt, rb: band.lb, lb: band.lb }
          : { lt: band.rt, rt: band.rt, rb: band.rb, lb: band.rb };
        const piece = this.addPiece(run, to);
        this.play(run, piece, [
          { clipPath: polygon(sliver), filter: 'brightness(1.35)' },
          { clipPath: polygon(band), filter: 'brightness(1)' }
        ], { duration: 420, delay: lead + 60 + (Math.abs(index - to) - 1) * 120, easing: 'cubic-bezier(.5,0,.3,1)' });
      }
    }

    // Liquid spill: liquid wells over the top of the band, runs sideways along
    // the top edge and pours down the image as a viscous curtain with thin
    // drips racing ahead. The shapes are white blobs in an SVG mask whose
    // "goo" filter (blur + alpha threshold) melts them together; a specular
    // pass adds the wet sheen and an offset copy a soft shadow under the
    // leading edge. Drawn in a 1152-unit-wide view box at the stage's ratio,
    // so the pour keeps the design's timing at any stage size.
    spill({ run, to, lead }) {
      const { width, height } = this.stageMetrics();
      const source = this.wrappers[to].querySelector('img');
      if (!width || !height || !source) return;
      const W = DESIGN.width;
      const H = (W * height) / width;
      const band = this.band(to);
      const lean = (this.slant * W) / H;            // a band edge moves this far left per unit down
      const centre = ((band.lt + band.rt) / 2) * W; // where the band meets the top edge
      const r = (n) => seeded(to * 211 + n);
      const speed = 2;                               // view-box units per ms along the top edge
      const top = -24;
      const rest = 6;                                // drop centres before they fall
      const fall = (H * 400) / DESIGN.height;        // drops fall past the bottom edge
      const arrive = (x) => Math.max(0, Math.abs(x - centre) - 90) / speed;
      const n1 = (value) => value.toFixed(1);

      // The curtain (29 wide drops) and the thin drips racing ahead (12).
      const drops = [];
      for (let i = 0; i < 29; i++) {
        const w = 58 + 14 * r(i + 40);
        drops.push({ x: -44 + i * 44 + (r(i) - 0.5) * 10, w, radius: w * 0.64, wait: 80, duration: 820 + 560 * r(i + 70), easing: 'cubic-bezier(.45,0,.55,1)' });
      }
      for (let d = 0; d < 12; d++) {
        const w = 22 + 8 * r(d + 120);
        drops.push({ x: 40 + (1072 * (d + r(d + 80))) / 12, w, radius: w * 0.88, wait: 30 + 140 * r(d + 140), duration: 380 + 240 * r(d + 160), easing: 'cubic-bezier(.5,0,.7,.4)' });
      }

      // The band itself (stretched past the top and bottom), the sideways run
      // along the top, and per drop a stem plus a falling head.
      const bandPoints = [
        [band.lt * W + 40 * lean, -40],
        [band.rt * W + 40 * lean, -40],
        [band.rb * W - 40 * lean, H + 40],
        [band.lb * W - 40 * lean, H + 40]
      ].map(([x, y]) => `${n1(x)},${n1(y)}`).join(' ');
      let shapes = `<polygon fill="#fff" points="${bandPoints}"/>`;
      shapes += `<rect data-q="sl" fill="#fff" x="-100" y="-60" width="${n1(centre + 100)}" height="74"/>`;
      shapes += `<rect data-q="sr" fill="#fff" x="${n1(centre)}" y="-60" width="${n1(W + 100 - centre)}" height="74"/>`;
      drops.forEach((drop, i) => {
        shapes += `<rect data-q="s${i}" fill="#fff" x="${n1(drop.x - drop.w / 2)}" y="${top}" width="${n1(drop.w)}" height="100"/>`;
        shapes += `<circle data-q="h${i}" fill="#fff" cx="${n1(drop.x)}" cy="${rest}" r="${n1(drop.radius)}"/>`;
      });

      const id = `ilf-tg-liquid-${++liquidCount}`;
      const region = `x="-120" y="-120" width="${W + 240}" height="${n1(H + 244)}"`;
      const box = `filterUnits="userSpaceOnUse" ${region} color-interpolation-filters="sRGB"`;
      const cover = `x="-100" y="-100" width="${W + 200}" height="${n1(H + 200)}"`;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'fx-liquid');
      svg.setAttribute('viewBox', `0 0 ${W} ${n1(H)}`);
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.innerHTML =
        '<defs>' +
        `<filter id="${id}-goo" ${box}><feGaussianBlur stdDeviation="7"/><feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 16 -6.5"/></filter>` +
        `<mask id="${id}-mask" maskUnits="userSpaceOnUse" ${region}><g filter="url(#${id}-goo)">${shapes}</g></mask>` +
        `<filter id="${id}-gloss" ${box}><feGaussianBlur in="SourceAlpha" stdDeviation="3" result="b"/>` +
        '<feSpecularLighting in="b" surfaceScale="5" specularConstant="1.05" specularExponent="24" lighting-color="#ffffff" result="s"><feDistantLight azimuth="75" elevation="38"/></feSpecularLighting>' +
        '<feComposite in="s" in2="SourceAlpha" operator="in"/></filter>' +
        `<filter id="${id}-shade" ${box}><feGaussianBlur stdDeviation="3"/></filter>` +
        '</defs>' +
        `<g data-q="shade" opacity="0" filter="url(#${id}-shade)"><rect ${cover} fill="#2B1813" fill-opacity="0.5" mask="url(#${id}-mask)" transform="translate(0 7)"/></g>` +
        `<image data-q="img" x="0" y="0" width="${W}" height="${n1(H)}" preserveAspectRatio="xMidYMid slice" mask="url(#${id}-mask)"/>` +
        `<g data-q="gloss" opacity="0" filter="url(#${id}-gloss)"><rect ${cover} fill="#fff" mask="url(#${id}-mask)"/></g>`;
      // The resource the band already shows, so no other size is downloaded.
      svg.querySelector('[data-q="img"]').setAttribute('href', source.currentSrc || source.src);
      this.fx.pieces.append(svg);
      run.pieces.push(svg);

      const part = (name) => svg.querySelector(`[data-q="${name}"]`);
      const linear = (duration, delay) => ({ duration, delay, easing: 'linear' });
      // Scaling about a point: move it to the origin, scale, move it back.
      const about = (x, y, scale) => `translate(${n1(x)}px, ${n1(y)}px) ${scale} translate(${n1(-x)}px, ${n1(-y)}px)`;
      this.play(run, part('sl'), [
        { transform: about(centre, 0, `scaleX(${Math.min(1, 90 / (centre + 100)).toFixed(4)})`) },
        { transform: about(centre, 0, 'scaleX(1)') }
      ], linear(Math.max(1, (centre + 10) / speed), lead));
      this.play(run, part('sr'), [
        { transform: about(centre, 0, `scaleX(${Math.min(1, 90 / (W + 100 - centre)).toFixed(4)})`) },
        { transform: about(centre, 0, 'scaleX(1)') }
      ], linear(Math.max(1, (W + 10 - centre) / speed), lead));

      let end = 0;
      drops.forEach((drop, i) => {
        const start = Math.max(0, arrive(drop.x) + drop.wait - 120);
        const total = 120 + drop.duration;
        const swell = 120 / total;
        const timing = linear(total, lead + start);
        this.play(run, part(`h${i}`), [
          { transform: `translateY(0px) ${about(drop.x, rest, 'scale(0)')}`, easing: 'ease-out' },
          { transform: `translateY(0px) ${about(drop.x, rest, 'scale(1)')}`, offset: swell, easing: drop.easing },
          { transform: `translateY(${n1(fall)}px) ${about(drop.x, rest, 'scale(1)')}` }
        ], timing);
        this.play(run, part(`s${i}`), [
          { transform: about(drop.x, top, 'scaleY(0.001)'), easing: 'ease-out' },
          { transform: about(drop.x, top, `scaleY(${((rest - top) / 100).toFixed(3)})`), offset: swell, easing: drop.easing },
          { transform: about(drop.x, top, `scaleY(${((rest - top + fall) / 100).toFixed(3)})`) }
        ], timing);
        end = Math.max(end, start + total);
      });
      this.play(run, part('shade'), [{ opacity: 0 }, { opacity: 1, offset: 0.1 }, { opacity: 1, offset: 0.8 }, { opacity: 0 }], linear(end, lead));
      this.play(run, part('gloss'), [{ opacity: 0 }, { opacity: 0.9, offset: 0.1 }, { opacity: 0.9, offset: 0.78 }, { opacity: 0 }], linear(end, lead));
    }

    // Soft spread: a feathered ellipse in the bands' slanted space (see
    // .fx-soft). It starts so tall that its sides read as the band's edges,
    // then widens while its height drops, so the band swells into a round glow.
    soft({ run, to, lead }) {
      const { width, height, sx, sy } = this.stageMetrics();
      const band = this.band(to);
      const centre = ((band.lt + band.rt) / 2) * width; // on the top edge, where the skew leaves x unchanged
      const half = ((band.rt - band.lt) / 2) * width;
      const piece = document.createElement('div');
      piece.className = 'fx-soft';
      piece.append(this.cloneImage(to));
      this.fx.pieces.append(piece);
      run.pieces.push(piece);
      const mask = (rx, ry) => {
        const size = `${(2 * rx).toFixed(1)}px ${(2 * ry).toFixed(1)}px`;
        const position = `${(centre - rx).toFixed(1)}px ${(height / 2 - ry).toFixed(1)}px`;
        return { maskSize: size, webkitMaskSize: size, maskPosition: position, webkitMaskPosition: position };
      };
      const timing = { duration: 1700, delay: lead, easing: 'cubic-bezier(.4,0,.3,1)' };
      this.play(run, piece, [
        mask(half, 2400 * sy),
        { ...mask(half + 170 * sx, 380 * sy), offset: 0.32 },
        { ...mask(half + 560 * sx, 520 * sy), offset: 0.62 },
        mask(2300 * sx, 1700 * sy)
      ], timing);
      this.play(run, piece, [
        { filter: 'brightness(1.22) saturate(1.1)' },
        { filter: 'brightness(1) saturate(1)' }
      ], { ...timing, easing: 'ease' });
    }

    // Twin laser: two scan lines sweep the band open over scan-line texture.
    laser({ run, target, band, start, to, lead }) {
      run.shape = 'band';
      const timing = { duration: 1250, delay: lead, easing: 'cubic-bezier(.45,0,.3,1)' };
      this.play(run, target, [{ clipPath: start || polygon(band) }, { clipPath: polygon(this.full) }], timing);
      if (this.fx.laserLabel) this.fx.laserLabel.textContent = `SCAN ${String(to + 1).padStart(2, '0')}`;
      this.moveEdges(run, this.fx.laserLeft, this.fx.laserRight, band, timing);
      this.play(run, this.fx.lines, [
        { opacity: 0 },
        { opacity: 0.6, offset: 0.15 },
        { opacity: 0.6, offset: 0.8 },
        { opacity: 0 }
      ], { ...timing, easing: 'ease' });
    }

    // Part the bands: the band opens and pushes the others off the stage
    // (from another tone: the previous tone sinks back instead).
    push({ run, target, band, start, to, fromAll, previous, lead }) {
      run.shape = 'band';
      const timing = { duration: 1100, delay: lead, easing: 'cubic-bezier(.77,0,.18,1)' };
      this.play(run, target, [{ clipPath: start || polygon(band) }, { clipPath: polygon(this.full) }], timing);
      this.moveEdges(run, this.fx.edgeLeft, this.fx.edgeRight, band, timing);
      if (fromAll) {
        // Each side travels with the edge that pushes it.
        const left = midLeft(this.full) - midLeft(band);
        const right = midRight(this.full) - midRight(band);
        this.wrappers.forEach((wrapper, index) => {
          if (index === to) return;
          this.play(run, wrapper, [
            { transform: 'translateX(0%)' },
            { transform: shiftX(index < to ? left : right) }
          ], timing);
        });
      } else {
        this.play(run, previous, [
          { transform: 'scale(1)', filter: 'brightness(1)' },
          { transform: 'scale(0.94)', filter: 'brightness(0.6)' }
        ], timing);
        // The sinking tone uncovers the stage itself, not the bands behind it.
        this.wrappers.forEach((wrapper, index) => {
          if (index === to || wrapper === previous) return;
          this.play(run, wrapper, [{ opacity: 0 }, { opacity: 0 }], timing);
        });
      }
    }

    // Venetian ripple: vertical strips (two per band) open from their middle,
    // rippling outward from the band.
    slices({ run, to, lead }) {
      const strips = this.count * 2;
      const center = (to + 0.5) * 2; // the band's centre, in strips
      this.fx.pieces.classList.add('fx-pieces--strips');
      for (let index = 0; index < strips; index++) {
        const delay = lead + Math.max(0, Math.abs(index + 0.5 - center) - 0.5) * 60;
        const strip = this.addCell(run, to, 'fx-strip', `translateX(${(-index * 100) / strips}%)`, delay + 650);
        this.play(run, strip, [
          { clipPath: 'inset(50% 0% 50% 0%)', filter: 'brightness(1.9)' },
          { clipPath: 'inset(0% 0% 0% 0%)', filter: 'brightness(1)' }
        ], { duration: 650, delay, easing: 'cubic-bezier(.6,0,.2,1)' });
      }
    }

    // Mosaic ripple: tiles flip in, outward from the band's slanted centre line.
    tiles({ run, to, lead }) {
      const { width } = this.stage.getBoundingClientRect();
      const [columns, rows] = width >= 750 ? [16, 4] : [8, 3];
      this.fx.pieces.classList.add('fx-pieces--tiles', `fx-pieces--${columns}x${rows}`);
      const center = (to + 0.5) / this.count;
      for (let index = 0; index < columns * rows; index++) {
        const column = index % columns;
        const row = Math.floor(index / columns);
        const x = (column + 0.5) / columns;
        const y = (row + 0.5) / rows;
        // The band's centre line at this tile's height.
        const bandX = center + this.slant / 2 - this.slant * y;
        const delay = lead + Math.round(Math.max(0, Math.abs(x - bandX) * columns - 1.25) * 50);
        const tile = this.addCell(
          run,
          to,
          'fx-tile',
          `translate(${(-column * 100) / columns}%, ${(-row * 100) / rows}%)`,
          delay + 560
        );
        this.play(run, tile, [
          { opacity: 0, transform: `perspective(900px) rotateY(${x < bandX ? 90 : -90}deg) scale(0.6)`, filter: 'brightness(1.7)' },
          { opacity: 1, transform: 'perspective(900px) rotateY(0deg) scale(1)', filter: 'brightness(1)' }
        ], { duration: 560, delay, easing: 'cubic-bezier(.2,.7,.2,1)' });
      }
    }

    // Origami unfold: each neighbouring band of the new tone swings open on a
    // hinge along the slanted edge it shares with the band before it.
    fold({ run, to, lead }) {
      const { tan, sx } = this.stageMetrics();
      const angle = Math.atan(tan);
      const axis = `${(-Math.sin(angle)).toFixed(4)}, ${Math.cos(angle).toFixed(4)}, 0`; // along the slanted edge
      const depth = Math.round(1500 * sx);
      const turn = (degrees) => `perspective(${depth}px) rotate3d(${axis}, ${degrees}deg)`;
      for (let index = 0; index < this.count; index++) {
        if (index === to) continue;
        const band = this.band(index);
        const side = index > to ? 1 : -1;
        const piece = this.addPiece(run, to);
        piece.classList.add('fx-fold');
        const hold = { clipPath: polygon(band), transformOrigin: `${pct(side > 0 ? midLeft(band) : midRight(band))} 50%` };
        this.play(run, piece, [
          { ...hold, transform: turn(side * 96), filter: 'brightness(0.3)', easing: 'cubic-bezier(.4,0,.3,1)' },
          { ...hold, transform: turn(side * -7), filter: 'brightness(1.14)', offset: 0.74, easing: 'ease-in-out' },
          { ...hold, transform: turn(0), filter: 'brightness(1)' }
        ], { duration: 600, delay: lead + (Math.abs(index - to) - 1) * 150, easing: 'linear' });
      }
    }

    // Crystal growth outline: jagged teeth along both band edges. `growth` is
    // in design pixels; below 0 the crystal has grown past the whole stage.
    crystalShape(index, growth) {
      const band = this.band(index);
      const teeth = 9;
      const reach = growth / DESIGN.width;
      const points = [];
      for (let i = 0; i <= 2 * teeth; i++) {
        const y = i / (2 * teeth);
        const f = i % 2 ? 0.7 + 0.9 * seeded(index * 50 + i) : 0.1 + 0.2 * seeded(index * 50 + 30 + i);
        points.push([growth < 0 ? 1500 / DESIGN.width : mix(band.rt, band.rb, y) + reach * f, y]);
      }
      for (let i = 2 * teeth; i >= 0; i--) {
        const y = i / (2 * teeth);
        const f = i % 2 ? 0.7 + 0.9 * seeded(index * 50 + 60 + i) : 0.1 + 0.2 * seeded(index * 50 + 90 + i);
        points.push([growth < 0 ? -350 / DESIGN.width : mix(band.lt, band.lb, y) - reach * f, y]);
      }
      return polygonPoints(points);
    }

    // Crystal growth: crystal teeth grow out of both band edges until they
    // cover the stage, the new tone frosty at first.
    crystal({ run, target, to, lead }) {
      const timing = { duration: 1500, delay: lead, easing: 'cubic-bezier(.45,0,.3,1)' };
      this.play(run, target, [
        { clipPath: this.crystalShape(to, 0) },
        { clipPath: this.crystalShape(to, 46), offset: 0.2 },
        { clipPath: this.crystalShape(to, 190), offset: 0.46 },
        { clipPath: this.crystalShape(to, 540), offset: 0.76 },
        { clipPath: this.crystalShape(to, -1) }
      ], timing);
      this.play(run, target, [
        { filter: 'brightness(1.32) saturate(0.75) contrast(1.08)' },
        { filter: 'brightness(1.12) saturate(0.9) contrast(1.04)', offset: 0.5 },
        { filter: 'brightness(1) saturate(1) contrast(1)' }
      ], { ...timing, easing: 'ease' });
    }

    // Glitch stream: horizontal scan strips shoot out of the band in
    // stuttering jumps with an RGB split that settles, over flickering scan lines.
    glitch({ run, to, lead }) {
      const { sx } = this.stageMetrics();
      const band = this.band(to);
      const rows = 14;
      this.fx.pieces.classList.add('fx-pieces--rows');
      let lastDelay = 0;
      for (let i = 0; i < rows; i++) {
        const row = document.createElement('div');
        row.className = 'fx-row';
        const image = this.cloneImage(to);
        row.append(image);
        this.fx.pieces.append(row);
        run.pieces.push(row);
        // The band at the strip's middle height, as left / right insets.
        const y = (i + 0.5) / rows;
        const left = Math.max(0, mix(band.lt, band.lb, y));
        const right = Math.max(0, 1 - mix(band.rt, band.rb, y));
        const r = (n) => seeded(to * 100 + i * 7 + n);
        const first = 0.16 + 0.1 * r(1);
        const settle = 0.6 + 0.12 * r(4);
        const a = 0.5 + 0.35 * r(2);
        const c = 0.5 + 0.35 * r(3);
        const delay = Math.round(r(10) * 240);
        lastDelay = Math.max(lastDelay, delay);
        const place = `translateY(${((-i * 100) / rows).toFixed(3)}%)`; // the image part behind this strip
        const frame = (insetLeft, insetRight, shift) => ({
          clipPath: `inset(0% ${pct(insetRight)} 0% ${pct(insetLeft)})`,
          transform: `${place} translateX(${(shift * sx).toFixed(1)}px)`
        });
        const jump = 'steps(2, end)';
        this.play(run, image, [
          { ...frame(left, right, 0), easing: jump },
          { ...frame(left * a, right * c, (r(7) - 0.5) * 30), offset: first, easing: jump },
          { ...frame(left * a, right * c, (r(8) - 0.5) * 18), offset: first + 0.16, easing: jump },
          { ...frame(left * 0.16 * r(5), right * 0.16 * r(6), (r(9) - 0.5) * 10), offset: settle, easing: 'cubic-bezier(.7,0,.2,1)' },
          frame(0, 0, 0)
        ], { duration: 840, delay: lead + delay, easing: 'linear' });
        const split = (d, alpha, brightness) =>
          `drop-shadow(${(d * sx).toFixed(1)}px 0 0 rgba(255, 40, 96, ${alpha})) drop-shadow(${(-d * sx).toFixed(1)}px 0 0 rgba(30, 220, 255, ${alpha})) brightness(${brightness})`;
        this.play(run, row, [
          { filter: split(7, 0.8, 1.35) },
          { filter: split(-4, 0.6, 1.12), offset: 0.5 },
          { filter: split(0, 0, 1) }
        ], { duration: 840, delay: lead + delay, easing: 'steps(6, end)' });
      }
      this.play(run, this.fx.lines, [
        { opacity: 0 },
        { opacity: 0.75, offset: 0.08 },
        { opacity: 0.25, offset: 0.3 },
        { opacity: 0.6, offset: 0.5 },
        { opacity: 0 }
      ], { duration: lastDelay + 840, delay: lead, easing: 'steps(8, end)' });
    }

    // Shatter fall outline: band `index` cracked into four irregular quads
    // around an off-centre point, as [x, y] stage fractions.
    shardsOf(index) {
      const band = this.band(index);
      const r = (n) => seeded(index * 17 + n);
      const top = [mix(band.lt, band.rt, 0.3 + 0.4 * r(1)), 0];
      const bottom = [mix(band.lb, band.rb, 0.3 + 0.4 * r(2)), 1];
      const leftAt = 0.3 + 0.4 * r(3);
      const rightAt = 0.3 + 0.4 * r(4);
      const left = [mix(band.lt, band.lb, leftAt), leftAt];
      const right = [mix(band.rt, band.rb, rightAt), rightAt];
      const centre = [
        (top[0] + bottom[0] + left[0] + right[0]) / 4 + ((r(5) - 0.5) * 30) / DESIGN.width,
        (left[1] + right[1]) / 2 + ((r(6) - 0.5) * 50) / DESIGN.height
      ];
      return [
        [[band.lt, 0], top, centre, left],
        [top, [band.rt, 0], right, centre],
        [left, centre, bottom, [band.lb, 1]],
        [centre, right, [band.rb, 1], bottom]
      ];
    }

    // Shatter fall: the old picture cracks band by band and drops away,
    // nearest bands first, off the new tone already in place underneath.
    shatter({ run, target, to, from, fromAll, lead }) {
      const { sx, sy } = this.stageMetrics();
      target.classList.add('is-full');
      for (let index = 0; index < this.count; index++) {
        if (index === to) continue;
        const side = index < to ? -1 : 1;
        this.shardsOf(index).forEach((points, n) => {
          // The old picture: each band's own tone, or the tone shown before.
          const piece = this.addPiece(run, fromAll ? index : from);
          const r = (m) => seeded((index * 4 + n) * 13 + m);
          const centroid = (axis) => points.reduce((sum, point) => sum + point[axis], 0) / points.length;
          const hold = { clipPath: polygonPoints(points), transformOrigin: `${pct(centroid(0))} ${pct(centroid(1))}` };
          const drift = side * (30 + 100 * r(2)) * sx;
          const spin = (r(3) > 0.3 ? side : -side) * (12 + 32 * r(4));
          const crack = side * (4 + 6 * r(5)) * sx;
          this.play(run, piece, [
            { ...hold, transform: 'translate(0px, 0px) rotate(0deg)', filter: 'brightness(1)', opacity: 1, easing: 'ease-out' },
            { ...hold, transform: `translate(${crack.toFixed(1)}px, ${(-4 * sy).toFixed(1)}px) rotate(${(spin * 0.06).toFixed(2)}deg)`, filter: 'brightness(1.28)', opacity: 1, offset: 0.16, easing: 'cubic-bezier(.55,0,.9,.45)' },
            { ...hold, transform: `translate(${drift.toFixed(1)}px, ${(480 * sy).toFixed(1)}px) rotate(${spin.toFixed(1)}deg)`, filter: 'brightness(0.7)', opacity: 0.25 }
          ], { duration: 950, delay: lead + (Math.abs(index - to) - 1) * 130 + Math.round(r(1) * 70), easing: 'linear' });
        });
      }
    }

    // A Fansly logo heart in the effect layer, above the edge glows; `shade`
    // picks its colors (.fx-heart--white / --accent / --deep).
    addHeart(run, shade) {
      const heart = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      heart.setAttribute('class', `fx-heart fx-heart--${shade}`);
      heart.setAttribute('viewBox', FANSLY_HEART.viewBox);
      heart.setAttribute('aria-hidden', 'true');
      heart.innerHTML = `<path d="${FANSLY_HEART.path}"/>`;
      this.fx.root.append(heart);
      run.pieces.push(heart);
      return heart;
    }

    // Heart flurry: the band opens like Band expand while a flurry of Fansly
    // logo hearts — white, accent and deep accent — floats up out of it.
    // Each heart starts at a point inside the band, swells, then drifts up,
    // sways and turns as it fades.
    flurry({ run, target, band, start, to, lead }) {
      run.shape = 'band';
      const timing = { duration: 1100, delay: lead, easing: 'cubic-bezier(.65,0,.35,1)' };
      this.play(run, target, [{ clipPath: start || polygon(band) }, { clipPath: polygon(this.full) }], timing);
      this.moveEdges(run, this.fx.edgeLeft, this.fx.edgeRight, band, timing);

      const { width, height, sx, sy } = this.stageMetrics();
      if (!width || !height || !this.fx.root) return;
      const largest = 40 * sx; // every heart's CSS size (see .fx-heart)
      const margin = 16 / DESIGN.width;
      const shades = ['white', 'accent', 'deep'];
      for (let i = 0; i < 16; i++) {
        const r = (n) => seeded(to * 97 + i * 11 + n);
        const y = (60 + 230 * r(6)) / DESIGN.height;
        const left = Math.max(margin, mix(band.lt, band.lb, y));
        const right = Math.min(1 - margin, mix(band.rt, band.rb, y));
        const x = mix(left, right, 0.15 + 0.7 * r(7));
        // Design size 18-40 px, never below 10 px on a small stage
        const size = Math.max((18 + 22 * r(8)) * sx, 10) / largest;
        const rise = (120 + 140 * r(2)) * sy;
        const sway = (r(3) - 0.5) * 70 * sx;
        const turn = (r(4) - 0.5) * 40;
        const place = (dx, dy, scale, degrees) =>
          `translate(${(x * width + dx).toFixed(1)}px, ${(y * height + dy).toFixed(1)}px) translate(-50%, -50%) scale(${(size * scale).toFixed(3)}) rotate(${degrees.toFixed(1)}deg)`;
        this.play(run, this.addHeart(run, shades[i % 3]), [
          { transform: place(0, 0, 0.2, 0), opacity: 0 },
          { transform: place(sway * 0.3, -rise * 0.25, 1.1, turn * 0.4), opacity: 1, offset: 0.22 },
          { transform: place(sway, -rise, 0.75, turn), opacity: 0 }
        ], { duration: 1000 + 400 * r(5), delay: lead + Math.round(r(1) * 650), easing: 'cubic-bezier(.2,.6,.3,1)' });
      }
    }
  }

  // Compute the dominant color of an image (no library): bucket pixels into
  // coarse RGB cells, then average the most populated cell. Loads a tiny copy
  // with crossOrigin so the canvas isn't tainted (Shopify's CDN sends CORS).
  function dominantColor(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const width = img.naturalWidth || 40;
        const height = img.naturalHeight || 40;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('no 2d context')); // keep the configured color
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        let pixels;
        try {
          pixels = ctx.getImageData(0, 0, width, height).data;
        } catch (error) {
          reject(error); // tainted canvas — keep the configured color
          return;
        }

        const buckets = new Map();
        let winner = null;
        for (let i = 0; i < pixels.length; i += 4) {
          if (pixels[i + 3] < 128) continue; // skip transparent pixels
          const red = pixels[i];
          const green = pixels[i + 1];
          const blue = pixels[i + 2];
          // quantize each channel to 32-wide cells so similar colors group up
          const key = `${red >> 5},${green >> 5},${blue >> 5}`;
          let bucket = buckets.get(key);
          if (!bucket) {
            bucket = { count: 0, red: 0, green: 0, blue: 0 };
            buckets.set(key, bucket);
          }
          bucket.count++;
          bucket.red += red;
          bucket.green += green;
          bucket.blue += blue;
          if (!winner || bucket.count > winner.count) winner = bucket;
        }

        if (!winner) {
          reject(new Error('no opaque pixels'));
          return;
        }
        const red = Math.round(winner.red / winner.count);
        const green = Math.round(winner.green / winner.count);
        const blue = Math.round(winner.blue / winner.count);
        resolve(`rgb(${red}, ${green}, ${blue})`);
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  // In "auto" mode, recolor each control button from its texture image's
  // dominant color. The color is data computed at runtime, so it is handed to
  // the CSS as the --button-color custom property the stylesheet already reads;
  // has-color ends the neutral placeholder, tone-dark switches names to light ink.
  function applyAutoButtonColors(root) {
    if (root.dataset.buttonColorMode !== 'auto') return;
    root.querySelectorAll('.ilf-control-btn[data-color-src]').forEach((button) => {
      dominantColor(button.dataset.colorSrc)
        .then((color) => {
          button.style.setProperty('--button-color', color);
          const [red, green, blue] = color.match(/\d+/g).map(Number);
          button.classList.toggle('tone-dark', (red * 299 + green * 587 + blue * 114) / 1000 < 140);
          button.classList.add('has-color');
        })
        .catch(() => {}); // on failure, keep the block's configured color
    });
  }

  // ---- Button styles with pointer-driven motion ----

  // Heat touch: the tone's color spreads from the point where the pointer
  // entered the tile (its centre when the tone is shown another way).
  class HeatButtons {
    constructor(buttons) {
      this.tiles = buttons.map((button) => ({
        face: button.querySelector('.btn-face'),
        heat: button.querySelector('.btn-heat'),
        entry: null,      // pointer position inside the tile, until the next spread
        origin: null,     // where the shown spread started
        shown: false,
        animation: null
      }));
      buttons.forEach((button, index) => {
        const remember = (event) => {
          const tile = this.tiles[index];
          const rect = tile.face.getBoundingClientRect();
          tile.entry = [event.clientX - rect.left, event.clientY - rect.top];
        };
        button.addEventListener('pointerenter', remember);
        button.addEventListener('pointerdown', remember);
      });
    }

    activeChanged(activeIndex) {
      this.tiles.forEach((tile, index) => {
        const shown = index === activeIndex;
        if (shown === tile.shown || !tile.heat) return;
        tile.shown = shown;
        if (shown) {
          const size = tile.face.offsetWidth;
          tile.origin = tile.entry || [size / 2, size / 2];
        }
        tile.entry = null;
        const [x, y] = tile.origin || [0, 0];
        const at = `translate(${x}px, ${y}px)`;
        const from = tile.animation ? getComputedStyle(tile.heat).transform : `${at} scale(0)`;
        if (tile.animation) tile.animation.cancel();
        tile.animation = tile.heat.animate(
          [{ transform: from }, { transform: `${at} scale(${shown ? 16 : 0})` }],
          { duration: reducedMotion() ? 0 : (shown ? 750 : 450), easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' }
        );
      });
    }
  }

  // Tilt glass: under a mouse the tile tilts toward the pointer and its glare
  // follows it; leaving eases both back to the tile's resting look.
  class TiltButtons {
    constructor(buttons) {
      buttons.forEach((button) => {
        const face = button.querySelector('.btn-face');
        const glare = button.querySelector('.btn-glare');
        const held = new Map(); // element → the animation holding its tilt

        const hold = (element, transform) => {
          if (!element) return;
          const animation = held.get(element);
          if (animation) {
            animation.effect.setKeyframes([{ transform }]);
          } else {
            held.set(element, element.animate([{ transform }], { duration: 120, easing: 'ease-out', fill: 'forwards' }));
          }
        };

        button.addEventListener('pointermove', (event) => {
          if (event.pointerType !== 'mouse' || reducedMotion()) return;
          const rect = face.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
          const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
          hold(face, `perspective(700px) rotateX(${((0.5 - y) * 22).toFixed(1)}deg) rotateY(${((x - 0.5) * 22).toFixed(1)}deg) translateY(${(-rect.height * 0.05).toFixed(1)}px) scale(1.06)`);
          hold(glare, `translate(${(x * rect.width).toFixed(1)}px, ${(y * rect.height).toFixed(1)}px)`);
        });

        button.addEventListener('pointerleave', () => {
          held.forEach((animation, element) => {
            const current = getComputedStyle(element).transform;
            animation.cancel();
            // A lone offset-0 keyframe animates from it to the resting style.
            element.animate([{ transform: current, offset: 0 }], { duration: 550, easing: 'cubic-bezier(.2,.7,.2,1)' });
          });
          held.clear();
        });
      });
    }

    activeChanged() {}
  }

  const BUTTON_FX = {
    'heat-touch': HeatButtons,
    'tilt-glass': TiltButtons
  };

  // ---- Tab switching: preload, wait until ready, crossfade ----

  const PANEL_READY_TIMEOUT = 1500; // ms a tab switch waits at most for the new panel's images
  const PANEL_FADE = 320;           // ms the new panel takes to fade in over the current one

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Lazy images in a hidden panel never start loading on their own; switching
  // them to eager starts the download while the panel stays hidden.
  function warmPanel(panel) {
    if (!panel || panel.dataset.warm) return;
    panel.dataset.warm = 'true';
    panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  }

  // Resolves once every slice image of the panel is decoded, or at the timeout.
  function panelReady(panel) {
    const images = Array.from(panel.querySelectorAll('.ilf-reveal-image-wrapper img'));
    const decoded = Promise.all(images.map((img) => img.decode().catch(() => {})));
    const timeout = new Promise((resolve) => setTimeout(resolve, PANEL_READY_TIMEOUT));
    return Promise.race([decoded, timeout]);
  }

  // The section root: owns a reveal per panel plus the tab bar.
  class IlfTextureGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const effect = this.dataset.effect;
      const Reveal = EFFECTS[effect] ? LayeredReveal : ClassicReveal;

      const ButtonFx = BUTTON_FX[this.dataset.buttonStyle];

      this.panels = Array.from(this.querySelectorAll('.ilf-tab-panel'));
      this.reveals = this.panels.map((panel) => {
        const reveal = new Reveal(panel, effect);
        if (ButtonFx) reveal.buttonFx = new ButtonFx(reveal.buttons);
        panel.__ilfReveal = reveal;
        return reveal;
      });

      applyAutoButtonColors(this);
      this.setupTabs();
    }

    disconnectedCallback() {
      if (this.reveals) this.reveals.forEach((reveal) => reveal.destroy());
      this.finishFade();
    }

    setupTabs() {
      this.tabs = Array.from(this.querySelectorAll('.ilf-tab'));
      if (this.tabs.length < 2) return; // single group => no switching to wire

      this.shownPanel = this.panels.find((panel) => !panel.hidden);
      this.switchToken = 0;
      this.fade = null;

      this.tabs.forEach((tab) => {
        const panel = this.panels.find((candidate) => candidate.dataset.tab === tab.dataset.tab);
        tab.addEventListener('click', () => this.showPanel(tab, panel));
        // Intent to switch: start loading that panel's images before the click.
        const warm = () => warmPanel(panel);
        tab.addEventListener('pointerenter', warm);
        tab.addEventListener('focus', warm);
        tab.addEventListener('touchstart', warm, { passive: true });
      });
    }

    async showPanel(tab, next) {
      if (!next || next === this.shownPanel) return;
      this.shownPanel = next;
      const token = ++this.switchToken;

      // The underline moves at once; the panel follows when its images are ready.
      this.tabs.forEach((other) => {
        const isActive = other === tab;
        other.classList.toggle('is-active', isActive);
        other.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const animate = !reducedMotion();
      warmPanel(next);
      if (animate) await panelReady(next);
      if (token !== this.switchToken) return; // a newer tab was chosen meanwhile

      this.finishFade();
      const outgoing = this.panels.filter((panel) => panel !== next && !panel.hidden);
      next.hidden = false;
      if (!animate || !outgoing.length) {
        outgoing.forEach((panel) => this.hidePanel(panel));
        return;
      }

      // Fade the new panel in on top of the current one, then hide the current one.
      next.classList.add('is-entering');
      outgoing.forEach((panel) => panel.classList.add('is-leaving'));
      const animation = next.animate([{ opacity: 0 }, { opacity: 1 }], { duration: PANEL_FADE, easing: 'ease-out' });
      this.fade = { animation, next, outgoing };
      animation.finished
        .then(() => {
          if (this.fade && this.fade.animation === animation) this.finishFade();
        })
        .catch(() => {}); // cancelled by a newer switch, which settled it
    }

    // Settle a crossfade at once: the new panel shown, the previous ones hidden.
    finishFade() {
      const fade = this.fade;
      if (!fade) return;
      this.fade = null;
      fade.animation.cancel();
      fade.next.classList.remove('is-entering');
      fade.outgoing.forEach((panel) => {
        panel.classList.remove('is-leaving');
        if (panel !== this.shownPanel) this.hidePanel(panel);
      });
    }

    hidePanel(panel) {
      panel.hidden = true;
      if (panel.__ilfReveal) panel.__ilfReveal.reset();
    }
  }

  customElements.define('ilf-texture-gallery', IlfTextureGallery);
})();
